import { classes } from 'html-classes';
import { observer } from 'mobx-react-lite';
import Skeleton from 'react-loading-skeleton';
import { Link } from 'react-router-dom';

import { company } from '~/company/Company';
import { useCategoryAnalytics } from '~/hooks/useCategoryAnalytics';
import { useGlobal } from '~/hooks/useGlobal';
import { useOnWarehouseChange } from '~/hooks/useOnWarehouseChange';
import { catalogStore, userStore } from '~/stores';
import type { Category } from '~/stores/CategoriesStore';

import CategoryBlockTitle from '../CategoryBlockTitle';
import { cfSrcForImage } from '../utils';

import styles from './CategoriesGridThreeMBlock.module.scss';

interface CategoriesGridThreeMBlockProps {
  category: Category;
  level?: number;
}

const CategoriesGridThreeMBlockProps = ({
  category,
  level = 2,
}: CategoriesGridThreeMBlockProps) => {
  const { handleClickCategory } = useCategoryAnalytics();
  const { isMobile } = useGlobal();

  useOnWarehouseChange((_, isChanged) => {
    if (isChanged) {
      category.productsRequest.run();
    }
  });

  const subcategories = (() => {
    if (level === 3) {
      const allChildren: Category[] = [];

      category.subcategory.forEach((secondLvlCategory) => {
        allChildren.push(...secondLvlCategory.subcategory);
      });

      return allChildren;
    }

    return category.subcategory;
  })();

  if (!subcategories.length || !subcategories[0]?.id) {
    return null;
  }

  return (
    <div
      key={category.id}
      className={classes([
        styles.container,
        company.isMarketplaceVariant && styles.containerMarketplace,
      ])}
      data-company={company.name}
    >
      {!(isMobile && company.isMarketplaceVariant) && (
        <CategoryBlockTitle text={category.name} />
      )}
      {subcategories.length > 0 ? (
        <div className={styles.tiles}>
          {subcategories.map(
            ({ name, previewImage, id, parentId, tileSize, products }) => {
              if (!parentId) {
                return null;
              }

              const tileSizeClassName = tileSize
                ? TILE_SIZE_CLASS_NAMES[tileSize]
                : undefined;

              return (
                <div
                  key={name}
                  className={classes([
                    styles.tile,
                    company.tileSizeEnabled
                      ? tileSizeClassName
                      : styles.tile__x1,
                  ])}
                  dir={userStore.dir}
                >
                  <Link
                    data-link={
                      company.isMarketplaceVariant
                        ? `category-${id}`
                        : undefined
                    }
                    state={{ clean_filters: true }}
                    to={`/c/${id}?showProducts=true`}
                    className={styles.link}
                    onClick={() => {
                      handleClickCategory(parentId)();
                      catalogStore.resetCatalogFilters();
                    }}
                  >
                    {company.isMarketplaceVariant ? (
                      <>
                        <div className={styles.title}>{name}</div>
                        {/* <div className={styles.productsCount}>
                          {(products?.length ?? 0).toString()} товаров
                        </div> */}
                        <img
                          className={styles.image}
                          src={cfSrcForImage(previewImage, {
                            fit: 'contain',
                            width: 512,
                            height: 484,
                          })}
                          alt={name}
                        />
                      </>
                    ) : (
                      <>
                        <div className={styles.imageBlock}>
                          <div className={styles.gradient} />
                          <img
                            className={styles.image}
                            src={cfSrcForImage(previewImage, {
                              fit: 'contain',
                              width: 512,
                              height: 484,
                            })}
                            alt={name}
                          />
                        </div>
                        <div className={styles.title}>{name}</div>
                      </>
                    )}
                  </Link>
                </div>
              );
            },
          )}
        </div>
      ) : (
        <Skeleton
          count={3}
          className={styles.tile}
          containerClassName={styles.tiles}
          inline={true}
        />
      )}
    </div>
  );
};

const TILE_SIZE_CLASS_NAMES: Record<number, string | undefined> = {
  1: styles.tile__x1,
  2: styles.tile__x2,
  3: styles.tile__x3,
};

export default observer(CategoriesGridThreeMBlockProps);
